import { createRootRoute, Outlet, retainSearchParams } from '@tanstack/react-router';
import { z } from 'zod';

import { RouteErrorBoundary } from '~/components/common/error-boundary/route-error-boundary';
import { NotFound } from '~/components/common/not-found/not-found';

/**
 * Root search schema. `sub` carries the active subsidiary id so authenticated
 * URLs are self-describing and shareable — a recipient lands in the same
 * subsidiary. It is optional (never forced onto public/existing links) and
 * `.catch(undefined)` so a malformed value degrades to "absent" instead of
 * throwing. The `_tenant` route's `reconcileSubsidiary` guard reads it and
 * switches subsidiary on mismatch. See docs/subsidiary-url-identifier.md.
 */
const rootSearchSchema = z.object({
  sub: z.string().optional().catch(undefined),
});

export const Route = createRootRoute({
  validateSearch: rootSearchSchema,
  // Auto-carry `sub` across every navigation/redirect without touching the
  // hundreds of Link/navigate call sites — the crux of the low-footprint scheme.
  search: {
    middlewares: [retainSearchParams(['sub'])],
  },
  component: RootLayout,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: NotFound,
});

/**
 * Root layout — renders the matched child route without any shell chrome.
 * CopilotShell (sidebar) is applied only to authenticated routes via _authenticated.tsx.
 */
function RootLayout() {
  return <Outlet />;
}
